import React, { useEffect, useMemo, useState } from 'react';
import './StreakCelebrateV2.css';

const COLORS = [
    '#5aab8a', // teal / fitness
    '#8fad6e', // green
    '#c4a56a', // gold
    '#78CCF6', // oola blue
    '#8a5a8a', // purple
    '#c47a5c', // coral
    '#b85a6e', // soft red
];

const SHAPES = ['dot', 'arc', 'stroke', 'spark', 'ring'];

function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function buildParticles(count = 20) {
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        shape: SHAPES[i % SHAPES.length],
        color: COLORS[i % COLORS.length],
        angle: (360 / count) * i + (i % 3) * 6,
        dist: 165 + (i % 6) * 32,
        delay: (i % 5) * 0.03,
        size: 10 + (i % 5) * 2.4,
        dur: 1.45 + (i % 4) * 0.1,
    }));
}

/**
 * Portal V2 — one-shot premium celebration particles (not traditional confetti).
 * Reuse on milestone screens: Step 1 score, Step 2 complete, Step 3 streak, etc.
 */
export default function StreakCelebrateV2({
    active = false,
    className = '',
}) {
    const [play, setPlay] = useState(false);
    const [spent, setSpent] = useState(false);
    const reduced = useMemo(() => prefersReducedMotion(), []);
    const particles = useMemo(() => buildParticles(20), []);

    useEffect(() => {
        if (!active || spent) return undefined;
        setPlay(true);
        const ms = reduced ? 900 : 1800;
        const t = window.setTimeout(() => {
            setPlay(false);
            setSpent(true);
        }, ms);
        return () => window.clearTimeout(t);
    }, [active, spent, reduced]);

    if (!play) return null;

    if (reduced) {
        return (
            <span
                className={`pv2-streak-celeb pv2-streak-celeb--pulse-only${
                    className ? ` ${className}` : ''
                }`}
                aria-hidden="true"
            >
                <span className="pv2-streak-celeb__pulse" />
            </span>
        );
    }

    return (
        <span
            className={`pv2-streak-celeb${className ? ` ${className}` : ''}`}
            aria-hidden="true"
        >
            <span className="pv2-streak-celeb__pulse" />
            {particles.map((p) => (
                <span
                    key={p.id}
                    className={`pv2-streak-celeb__p pv2-streak-celeb__p--${p.shape}`}
                    style={{
                        '--a': `${p.angle}deg`,
                        '--d': `${p.dist}px`,
                        '--delay': `${p.delay}s`,
                        '--dur': `${p.dur}s`,
                        '--c': p.color,
                        '--s': `${p.size}px`,
                    }}
                />
            ))}
        </span>
    );
}
